import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-ai-chat-button',
  imports: [MatTooltipModule],
  template: `
    <button
      type="button"
      class="btn header-button"
      aria-label="Open TaxTank AI assistant"
      aria-haspopup="dialog"
      [disabled]="loading()"
      matTooltip="TaxTank AI assistant"
      (click)="openChat()">
      <span class="fw-bold">AI</span>
    </button>
    @if (loadError()) {
      <span role="alert" class="fs-small">Unable to open AI chat. Please try again.</span>
    }
  `,
  styles: [':host { display: flex; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatButtonComponent {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  readonly loading = signal(false);
  readonly loadError = signal(false);

  async openChat(): Promise<void> {
    if (this.loading() || this.dialog.getDialogById('taxtank-ai-chat')) {
      return;
    }

    this.loading.set(true);
    this.loadError.set(false);

    try {
      const { AiChatDialogComponent: chatDialogComponent } = await import('./ai-chat-dialog.component');
      if (this.destroyRef.destroyed) {
        return;
      }

      this.dialog.open(chatDialogComponent, {
        id: 'taxtank-ai-chat',
        ariaLabelledBy: 'taxtank-ai-chat-title',
        width: '800px',
        maxWidth: 'calc(100vw - 32px)',
        height: 'min(720px, calc(100dvh - 32px))',
        position: { right: '16px', bottom: '16px' },
        autoFocus: 'first-tabbable',
        restoreFocus: true,
      });
    } catch {
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
